/**
 * Registry aggregation (architecture §5.4/§6.1; OpenSpec
 * `monorepo-shell-registry` task G2).
 *
 * Takes the generated module list (`registry.gen.ts`, task G1), walks every
 * export of every `./contributions` door, validates each descriptor, and
 * partitions the survivors by kind. Pure and framework-free: no React, no
 * `next/dynamic`, no atoms — so it is unit-testable, and so the same function
 * backs both the runtime host (which tolerates problems) and the contribution
 * CI (which fails on them).
 *
 * **Problems are collected, never thrown.** A malformed contribution must not
 * take the app down at startup — the offender is dropped and reported, while
 * CI (tasks C1–C2) turns the same report into a failed build. One validator,
 * two severities, no drift.
 */
import {
  isAnalyticsEventContribution,
  isFeatureFlagContribution,
  isNavEntryContribution,
  isQueuedModalContribution,
  isSettingsScreenContribution,
  isShellContribution,
  type AnalyticsEventContribution,
  type ContributionBuilds,
  type ContributionModule,
  type FeatureFlagContribution,
  type NavEntryContribution,
  type QueuedModalContribution,
  type SettingsScreenContribution,
} from '@heidi/runtime/shell';

import { navSlotMap, settingsSlotMap, type SlotMap } from './slot-maps';

/** Which shell is composing: the cloud/desktop app, or the on-prem appliance. */
export type BuildVariant = 'cloud' | 'on-prem';

export type RegistryProblemCode =
  | 'not-a-contribution'
  | 'duplicate-id'
  | 'duplicate-priority'
  | 'duplicate-flag-key'
  | 'duplicate-event-name'
  | 'unknown-section'
  | 'order-outside-band';

export interface RegistryProblem {
  readonly code: RegistryProblemCode;
  /** Package whose door exported the offender. */
  readonly package: string;
  /** Export name inside that door. */
  readonly exportName: string;
  readonly message: string;
}

/** A descriptor plus the package that contributed it (for manifests + errors). */
export type Owned<T> = T & { readonly ownerPackage: string };

export interface AppRegistry {
  /** Launch modals, highest frozen priority first. */
  readonly modals: readonly Owned<QueuedModalContribution>[];
  readonly settingsScreens: readonly Owned<SettingsScreenContribution>[];
  readonly navEntries: readonly Owned<NavEntryContribution>[];
  /** Feature-flag declarations, sorted by key. */
  readonly featureFlags: readonly Owned<FeatureFlagContribution>[];
  /** Frozen analytics event-name declarations, sorted by name. */
  readonly analyticsEvents: readonly Owned<AnalyticsEventContribution>[];
  readonly problems: readonly RegistryProblem[];
}

export interface BuildAppRegistryOptions {
  /** Defaults to `'cloud'` — the shell that aggregates everything. */
  readonly variant?: BuildVariant;
  readonly settingsSlots?: SlotMap;
  readonly navSlots?: SlotMap;
}

/**
 * D6: an omitted `builds` means `'cloud-only'`, so a forgotten declaration
 * withholds a surface from on-prem (visible in the manifest diff) rather than
 * leaking a cloud surface into the airgapped appliance.
 */
const reachesVariant = (builds: ContributionBuilds | undefined, variant: BuildVariant) =>
  variant === 'cloud' || (builds ?? 'cloud-only') === 'all';

const placementProblem = (
  descriptor: SettingsScreenContribution | NavEntryContribution,
  slots: SlotMap,
  origin: { package: string; exportName: string }
): RegistryProblem | null => {
  const band = slots.orderBands[descriptor.section];
  if (!slots.sections.includes(descriptor.section) || !band) {
    return {
      code: 'unknown-section',
      ...origin,
      message:
        `"${descriptor.id}" declares section "${descriptor.section}", which the slot map ` +
        `does not declare (sections: ${slots.sections.length ? slots.sections.join(', ') : 'none yet'}). ` +
        'Sections are a product/design decision — declare it in src/composition/slot-maps.ts with its owner.',
    };
  }
  const [min, max] = band;
  if (descriptor.order < min || descriptor.order > max) {
    return {
      code: 'order-outside-band',
      ...origin,
      message: `"${descriptor.id}" declares order ${descriptor.order}, outside section "${descriptor.section}" band [${min}, ${max}].`,
    };
  }
  return null;
};

export function buildAppRegistry(
  modules: readonly ContributionModule[],
  options: BuildAppRegistryOptions = {}
): AppRegistry {
  const {
    variant = 'cloud',
    settingsSlots = settingsSlotMap,
    navSlots = navSlotMap,
  } = options;

  const modals: Owned<QueuedModalContribution>[] = [];
  const settingsScreens: Owned<SettingsScreenContribution>[] = [];
  const navEntries: Owned<NavEntryContribution>[] = [];
  const featureFlags: Owned<FeatureFlagContribution>[] = [];
  const analyticsEvents: Owned<AnalyticsEventContribution>[] = [];
  const problems: RegistryProblem[] = [];

  const seenIds = new Map<string, string>(); // id → "package.exportName"
  const seenPriorities = new Map<number, string>();
  const seenFlagKeys = new Map<string, string>();
  const seenEventNames = new Map<string, string>();

  for (const { package: ownerPackage, module } of modules) {
    // Sorted so a door's export order cannot influence the registry.
    for (const exportName of Object.keys(module).sort()) {
      const value = module[exportName];
      const origin = { package: ownerPackage, exportName };

      if (!isShellContribution(value)) {
        problems.push({
          code: 'not-a-contribution',
          ...origin,
          message:
            'every ./contributions export must be a descriptor built by defineQueuedModal, ' +
            'defineSettingsScreen, defineNavEntry, defineFeatureFlag or defineAnalyticsEvent.',
        });
        continue;
      }

      if (!reachesVariant(value.builds, variant)) continue;

      const where = `${ownerPackage}.${exportName}`;

      // Flags carry a key, not an id — their identity namespace is the external
      // flag system's, so they partition before the id-keyed kinds.
      if (isFeatureFlagContribution(value)) {
        const keyClash = seenFlagKeys.get(value.key);
        if (keyClash) {
          problems.push({
            code: 'duplicate-flag-key',
            ...origin,
            message: `flag key "${value.key}" is already contributed by ${keyClash}.`,
          });
          continue;
        }
        seenFlagKeys.set(value.key, where);
        featureFlags.push({ ...value, ownerPackage });
        continue;
      }

      // Events partition here for the same reason flags do: identity is the
      // frozen `name`, in the warehouse's namespace rather than the queue's.
      // The D9 uniqueness guarantee is this check — one event name, one owning
      // package — and the event-reference gate's third arm re-asserts it from
      // source, so a collision fails whether or not the door is aggregated.
      if (isAnalyticsEventContribution(value)) {
        const nameClash = seenEventNames.get(value.name);
        if (nameClash) {
          problems.push({
            code: 'duplicate-event-name',
            ...origin,
            message: `analytics event "${value.name}" is already declared by ${nameClash}.`,
          });
          continue;
        }
        seenEventNames.set(value.name, where);
        analyticsEvents.push({ ...value, ownerPackage });
        continue;
      }

      const collision = seenIds.get(value.id);
      if (collision) {
        problems.push({
          code: 'duplicate-id',
          ...origin,
          message: `id "${value.id}" is already contributed by ${collision}.`,
        });
        continue;
      }
      seenIds.set(value.id, where);

      if (isQueuedModalContribution(value)) {
        // D5: uniqueness replaces tie-breaking, so aggregation order can never
        // influence arbitration once the legacy array is gone.
        const priorityClash = seenPriorities.get(value.priority);
        if (priorityClash) {
          problems.push({
            code: 'duplicate-priority',
            ...origin,
            message: `priority ${value.priority} is already taken by ${priorityClash}.`,
          });
          continue;
        }
        seenPriorities.set(value.priority, where);
        modals.push({ ...value, ownerPackage });
        continue;
      }

      const isSettings = isSettingsScreenContribution(value);
      const problem = placementProblem(
        value,
        isSettings ? settingsSlots : navSlots,
        origin
      );
      if (problem) {
        problems.push(problem);
        continue;
      }
      if (isSettings) settingsScreens.push({ ...value, ownerPackage });
      else if (isNavEntryContribution(value)) navEntries.push({ ...value, ownerPackage });
    }
  }

  const byPlacement = <T extends { section: string; order: number; id: string }>(
    slots: SlotMap
  ) => {
    return (a: T, b: T) => {
      const sectionDelta =
        slots.sections.indexOf(a.section) - slots.sections.indexOf(b.section);
      return sectionDelta !== 0
        ? sectionDelta
        : a.order - b.order || a.id.localeCompare(b.id);
    };
  };

  return {
    // Descending priority — the legacy arbiter's order, now tie-free.
    modals: [...modals].sort((a, b) => b.priority - a.priority),
    settingsScreens: [...settingsScreens].sort(byPlacement(settingsSlots)),
    navEntries: [...navEntries].sort(byPlacement(navSlots)),
    featureFlags: [...featureFlags].sort((a, b) => a.key.localeCompare(b.key)),
    analyticsEvents: [...analyticsEvents].sort((a, b) => a.name.localeCompare(b.name)),
    problems,
  };
}
